import type {
    IRegisterCustomer,
    IAuth,
    IAuthResponse,
    IChangeUserLocale,
    IChangeCustomerPassword, IEmailCheckResponse, IResetPassword, IResetPasswordResponse,
} from '@shared/auth';
import {
    ACCESS_TOKEN_KEY,
    CUSTOMER_ID_KEY,
    IS_LOGIN_BY_GOOGLE,
    LOCALE_KEY,
    SESSION_COOKIE_KEY,
} from '~/utils/defs';
import {useUserStore} from './user';
import type {IGoogleAuth} from '@shared/auth';
import type {ICustomer, IUpdateCustomerProfile} from '@shared/customer';
import type {IAuthUser} from '@shared/user';
import {useCustomerFavoriteStore} from './customer-favorite';

const ENDPOINT_PREFIX = 'auth';

export const useAuthStore = defineStore('auth', () => {
    const apiClient = useApiClient();
    const {setItem, getItem, clear, removeItem} = useLocalStorage();
    const accessToken = ref<string>('');
    const userStore = useUserStore();
    const profile = ref<ICustomer | null>(null);
    const favsStore = useCustomerFavoriteStore();
    const isRegisteredByGoogle = ref<boolean>(false);

    const checkEmail = async(email: string): Promise<boolean> => {
        const result = await apiClient.post<{ email: string }, IEmailCheckResponse>
        (ENDPOINT_PREFIX + '/check-email', {email});
        return Boolean(result?.data?.emailExist);
    };

    const resetPassword = async(payload: IResetPassword) => {
        return await apiClient.post<IResetPassword, IResetPasswordResponse>(ENDPOINT_PREFIX + '/generate-otp', payload);
    }

    const login = async(payload: IAuth) => {
        const result = await apiClient.post<IAuth, IAuthResponse>(ENDPOINT_PREFIX + '/login', payload);
        if (result.success) {
            const data = result.data as IAuthResponse;
            userStore.setUser(data.user as IAuthUser);
            setItem(LOCALE_KEY, data.user?.locale as string);
            setItem(ACCESS_TOKEN_KEY, data.access_token as string);
            accessToken.value = data.access_token as string;
            const fav = getItem(FAVORITE_ITEM_KEY);
            if (fav) removeItem(FAVORITE_ITEM_KEY);
            if (data.customerId) setItem(CUSTOMER_ID_KEY, data.customerId);
            favsStore.clearFavorites();
        }
        return result;
    };

    const changeLocale = async(payload: IChangeUserLocale) => {
        const result = await apiClient.post<IChangeUserLocale, IAuthResponse>(ENDPOINT_PREFIX + '/change-locale', payload);
        if (result.success) {
            const data = result.data as IAuthResponse;
            userStore.setUser(data.user as IAuthUser);
            setItem(ACCESS_TOKEN_KEY, data.access_token as string);
            accessToken.value = data.access_token as string;
            setItem(LOCALE_KEY, payload.locale);
        }
        return result;
    };

    const logout = async() => {
        const result = await apiClient.post(ENDPOINT_PREFIX + '/logout');
        if (result.success) {
            const cookie = useCookie(SESSION_COOKIE_KEY);
            accessToken.value = '';
            userStore.removeUser();
            cookie.value = null;
            favsStore.clearFavorites();
            clear();
        }
        return result;
    };

    const register = async(payload: IRegisterCustomer) => {
        const result = await apiClient.post<IRegisterCustomer, IAuthResponse>(ENDPOINT_PREFIX + '/register', payload);
        if (result.success) {
            const data = result.data as IAuthResponse;
            userStore.setUser(data.user as IAuthUser);
            setItem(ACCESS_TOKEN_KEY, data.access_token as string);
            setItem(LOCALE_KEY, data.user?.locale as string);
            accessToken.value = data.access_token as string;
            const fav = getItem(FAVORITE_ITEM_KEY);
            if (fav) removeItem(FAVORITE_ITEM_KEY);
            if (data.customerId) setItem(CUSTOMER_ID_KEY, data.customerId);
            favsStore.clearFavorites();
        }
        return result;
    };

    const updateProfile = async(payload: IUpdateCustomerProfile) => {
        const result = await apiClient.put<IUpdateCustomerProfile, ICustomer>(ENDPOINT_PREFIX + '/customer-profile', payload);
        if (result.success) profile.value = result.data;
        return result;
    };

    const getProfile = async() => {
        const result = await apiClient.get<ICustomer>(ENDPOINT_PREFIX + '/get-customer');
        if (result.success) {
            profile.value = result.data;
            if (profile.value?.icon) setItem(USER_ICON_KEY, profile.value.icon);
        }
        return result;
    };

    const changePassword = async(payload: IChangeCustomerPassword) => {
        return await apiClient.post<IChangeCustomerPassword, IAuthResponse>(ENDPOINT_PREFIX + '/change-password', payload);
    };

    const getAccessToken = () => {
        return accessToken.value ?? getItem(ACCESS_TOKEN_KEY);
    };

    const googleLogin = async(payload: IGoogleAuth) => {
        const result = await apiClient.post<IGoogleAuth, IAuthResponse>(ENDPOINT_PREFIX + '/google-login', payload);
        if (result.success) {
            const data = result.data as IAuthResponse;
            userStore.setUser(data.user as IAuthUser);
            setItem(LOCALE_KEY, data.user?.locale as string);
            setItem(ACCESS_TOKEN_KEY, data.access_token as string);
            accessToken.value = data.access_token as string;
            const fav = getItem(FAVORITE_ITEM_KEY);
            if (fav) removeItem(FAVORITE_ITEM_KEY);
            if (data.customerId) setItem(CUSTOMER_ID_KEY, data.customerId);
        }
        return result;
    };

    const approveRegisterByGoogle = (data: boolean) => {
        isRegisteredByGoogle.value = data;
        setItem(IS_LOGIN_BY_GOOGLE, data ? 'y' : 'n');
    };

    const initIsRegisteredByGoogle = () => {
        const item = getItem(IS_LOGIN_BY_GOOGLE, 'n');
        isRegisteredByGoogle.value = item === 'y';
    };

    return {
        login,
        logout,
        register,
        resetPassword,
        getAccessToken,
        changeLocale,
        updateProfile,
        checkEmail,
        profile,
        getProfile,
        changePassword,
        googleLogin,
        approveRegisterByGoogle,
        isRegisteredByGoogle,
        initIsRegisteredByGoogle,
    };
});
